// Pure: decides which Recently Deleted rows the unattended auto-purge may
// PERMANENTLY destroy. No React, no Firestore.
//
// This exists because that purge is the only code in the app that hard-deletes
// anything, it runs on its own whenever someone merely OPENS the page, and it
// asks nobody first. It deserves to be readable and testable in isolation.
//
// The 30-day rule is a DEALS-ONLY policy and must stay that way. Leads have
// never had a trash-expiry policy — on 2026-08-10 the live database held 26
// soft-deleted listing leads, most deleted back in June. Applying the deals
// retention rule to them would have hard-deleted months of history, unattended
// and unconfirmed, the first time anyone visited the page. Leads therefore
// never expire; a human clicking "Delete forever" is the only thing that
// destroys one.

export const RETENTION_DAYS = 30;
export const RETENTION_MS = RETENTION_DAYS * 24 * 60 * 60 * 1000;

// A live lead's restore point is stored as a SOFT-DELETED deal, not a
// human-deleted file. Two kinds exist:
//   - a Buyer/Listing Request intake stub (requestType "fileRequest")
//   - a board deal that was moved into a pipeline (migratedToLeadId set)
// Both are soft-deleted at creation and restored to the board when the lead
// signs, via the lead's sourceDealId. They must never be auto-purged (leads
// routinely sit longer than 30 days before signing) and must not appear in the
// Recently Deleted list as human-deletable files. sourceLeadId alone is NOT a
// stub marker: it is also set on real, restored board deals, which stay purgeable
// like any other human-deleted file.
export const isLeadBackingStub = (row) =>
  !!row && (row.requestType === "fileRequest" || !!row.migratedToLeadId);

// A row is only ever purgeable if it is a deal AND carries a sane, positive
// timestamp. A missing/zero/malformed deletedAt must never read as "infinitely
// old" — `now - 0` is ~57 years, which would mean instant destruction.
export const isPurgeable = (row, now = Date.now(), retentionMs = RETENTION_MS) => {
  if (!row || row._kind !== "deal") return false;
  // Never destroy a live lead's hidden restore point.
  if (isLeadBackingStub(row)) return false;
  const at = Number(row.deletedAt);
  if (!Number.isFinite(at) || at <= 0) return false;
  return now - at > retentionMs;
};

// Returns { expired, kept }. `kept` is the exact complement of `expired`, so a
// row can never fall through both filters and silently vanish from the page.
export const splitForPurge = (rows = [], now = Date.now(), retentionMs = RETENTION_MS) => {
  const expired = [];
  const kept = [];
  for (const row of rows) {
    (isPurgeable(row, now, retentionMs) ? expired : kept).push(row);
  }
  return { expired, kept };
};
